import { useEffect, useState } from "react";
import axios from "axios";
import { GraduationCap, Plus, Edit3, Trash2, X, RefreshCw,
         Users, Send, CheckCircle } from "lucide-react";

const emptyChild = {
  first_name: "", last_name: "", age: "", class_level: "",
  school_name: "", school_year: "2026-2027",
  monthly_need_fcfa: 500, yearly_target_fcfa: "",
  story: "", parent_relation: "",
};

export default function SponsorshipAdminPanel({ API }) {
  const pin = "SAFUAYE2024";
  const [tab, setTab] = useState("children");     // children | pledges
  const [children, setChildren] = useState([]);
  const [pledges, setPledges] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyChild);
  const [busy, setBusy] = useState("");

  const loadChildren = async () => {
    try {
      const r = await axios.get(`${API}/sponsorship/admin/children`, { params: { pin } });
      setChildren(r.data.items || []);
    } catch (e) { console.error(e); }
  };
  const loadPledges = async () => {
    try {
      const r = await axios.get(`${API}/sponsorship/admin/pledges`, { params: { pin } });
      setPledges(r.data.items || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { loadChildren(); loadPledges(); }, []); // eslint-disable-line

  const openCreate = () => { setEditing(null); setForm(emptyChild); setShowForm(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...emptyChild, ...c, age: c.age || "", yearly_target_fcfa: c.yearly_target_fcfa || "" });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      alert("Nom et prénoms obligatoires."); return;
    }
    setBusy("save");
    try {
      const payload = {
        ...form, pin,
        age: form.age ? Number(form.age) : null,
        monthly_need_fcfa: Number(form.monthly_need_fcfa) || 500,
        yearly_target_fcfa: form.yearly_target_fcfa ? Number(form.yearly_target_fcfa) : null,
      };
      if (editing) {
        await axios.put(`${API}/sponsorship/admin/children/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/sponsorship/admin/children`, payload);
      }
      await loadChildren(); setShowForm(false);
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  const removeChild = async (c) => {
    if (!window.confirm(`Retirer ${c.first_name} ${c.last_name} du programme ?`)) return;
    await axios.delete(`${API}/sponsorship/admin/children/${c.id}`, { params: { pin } });
    await loadChildren();
  };

  const seedAmelete = async () => {
    setBusy("seed");
    try {
      const r = await axios.post(`${API}/sponsorship/admin/seed`, null, { params: { pin } });
      alert(r.data.already_exists
        ? "AMELETE Piyalo Marina est déjà dans le programme."
        : "✅ AMELETE Piyalo Marina ajoutée au programme !");
      await loadChildren();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  const updatePledgeStatus = async (p, status) => {
    setBusy(`pl-${p.id}`);
    try {
      await axios.put(`${API}/sponsorship/admin/pledges/${p.id}`, { pin, status });
      await loadPledges(); await loadChildren();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  const STATUS_COLORS = {
    pending: "bg-amber-100 text-amber-800",
    confirmed: "bg-blue-100 text-blue-800",
    active: "bg-emerald-100 text-emerald-800",
    ended: "bg-gray-100 text-gray-700",
    cancelled: "bg-red-100 text-red-800",
  };

  return (
    <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-amber-50 border-2 border-indigo-300 rounded-2xl p-5 mb-6"
         data-testid="sponsorship-admin-panel">
      <div className="flex items-start gap-3 mb-4">
        <GraduationCap className="w-8 h-8 text-indigo-700" />
        <div className="flex-1">
          <h4 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2 flex-wrap">
            Programme Parrainage Scolaire
            <span className="text-xs bg-indigo-700 text-white px-2 py-0.5 rounded font-semibold">🆕 Public</span>
          </h4>
          <p className="text-xs text-gray-600 mt-1">
            Roster d'enfants à parrainer (visible publiquement sur <code>/#parrainage</code>) et gestion des engagements donateurs.
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("children")}
                className={`px-3 py-1.5 rounded font-semibold text-sm ${
                  tab === "children" ? "bg-indigo-700 text-white" : "bg-white border border-gray-300 text-gray-700"
                }`}
                data-testid="sponsor-admin-tab-children">
          👦 Enfants ({children.filter(c => c.active).length})
        </button>
        <button onClick={() => setTab("pledges")}
                className={`px-3 py-1.5 rounded font-semibold text-sm ${
                  tab === "pledges" ? "bg-indigo-700 text-white" : "bg-white border border-gray-300 text-gray-700"
                }`}
                data-testid="sponsor-admin-tab-pledges">
          💌 Engagements ({pledges.length})
        </button>
      </div>

      {tab === "children" && (
        <>
          <div className="flex flex-wrap gap-2 mb-3">
            <button onClick={openCreate}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded text-sm font-semibold flex items-center gap-2"
                    data-testid="sponsor-add-child-btn">
              <Plus className="w-4 h-4" /> Nouvel enfant
            </button>
            <button onClick={seedAmelete} disabled={busy === "seed"}
                    className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 text-white px-3 py-2 rounded text-sm font-semibold">
              🎒 Seed AMELETE Piyalo Marina
            </button>
            <button onClick={loadChildren}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-2 rounded text-sm font-semibold flex items-center gap-2">
              <RefreshCw className="w-4 h-4" /> Rafraîchir
            </button>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 max-h-[380px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="p-2 text-left">Nom</th>
                  <th className="p-2 text-left">Classe · École</th>
                  <th className="p-2 text-right">Mensuel</th>
                  <th className="p-2 text-right">Reçu / Objectif</th>
                  <th className="p-2 text-left">Statut</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {children.length === 0 && (
                  <tr><td colSpan={6} className="p-4 text-center text-gray-500">
                    Aucun enfant dans le programme. Cliquez sur "Seed" ou "Nouvel enfant".
                  </td></tr>
                )}
                {children.map(c => (
                  <tr key={c.id} className={`border-t border-gray-100 ${!c.active ? "opacity-40" : "hover:bg-indigo-50"}`}>
                    <td className="p-2 font-medium">{c.last_name} {c.first_name}
                      {c.age ? <span className="text-xs text-gray-500 ml-1">· {c.age} ans</span> : null}</td>
                    <td className="p-2 text-gray-700">
                      {c.class_level || "—"}
                      {c.school_name && <div className="text-xs text-gray-500">{c.school_name}</div>}
                    </td>
                    <td className="p-2 text-right font-semibold">{(c.monthly_need_fcfa || 0).toLocaleString("fr-FR")}</td>
                    <td className="p-2 text-right text-xs">
                      <span className="text-emerald-700">{(c.total_received_fcfa || 0).toLocaleString("fr-FR")}</span>
                      <span className="text-gray-500"> / {(c.yearly_target_fcfa || 0).toLocaleString("fr-FR")}</span>
                    </td>
                    <td className="p-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        c.status === "available" ? "bg-emerald-100 text-emerald-800"
                        : c.status === "sponsored" ? "bg-amber-100 text-amber-800"
                        : "bg-purple-100 text-purple-800"
                      }`}>{c.status}</span>
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(c)} className="text-blue-600 hover:text-blue-800 mr-2"
                              data-testid={`sponsor-edit-child-${c.id}`}>
                        <Edit3 className="w-4 h-4 inline" />
                      </button>
                      {c.active && (
                        <button onClick={() => removeChild(c)} className="text-red-600 hover:text-red-800">
                          <Trash2 className="w-4 h-4 inline" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "pledges" && (
        <div className="bg-white rounded-lg border border-gray-200 max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-left">Donateur</th>
                <th className="p-2 text-left">Enfant</th>
                <th className="p-2 text-right">Mensuel</th>
                <th className="p-2 text-right">Total</th>
                <th className="p-2 text-left">Statut</th>
                <th className="p-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pledges.length === 0 && (
                <tr><td colSpan={7} className="p-4 text-center text-gray-500">
                  Aucun engagement de donateur pour l'instant.
                </td></tr>
              )}
              {pledges.map(p => (
                <tr key={p.id} className="border-t border-gray-100 hover:bg-indigo-50">
                  <td className="p-2 text-xs">{(p.created_at || "").slice(0, 10)}</td>
                  <td className="p-2">
                    <div className="font-medium">{p.sponsor_name}</div>
                    <div className="text-xs text-gray-600">{p.sponsor_email}</div>
                    {p.sponsor_country && <div className="text-xs text-gray-500">{p.sponsor_country}</div>}
                  </td>
                  <td className="p-2 text-gray-700">{p.child_display || "—"}</td>
                  <td className="p-2 text-right font-semibold">{(p.monthly_amount_fcfa || 0).toLocaleString("fr-FR")}</td>
                  <td className="p-2 text-right text-indigo-700 font-bold">{(p.total_pledge_fcfa || 0).toLocaleString("fr-FR")}</td>
                  <td className="p-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[p.status] || "bg-gray-100"}`}>
                      {p.status}
                    </span>
                    {p.commit_months && <div className="text-xs text-gray-500 mt-1">{p.commit_months} mois</div>}
                  </td>
                  <td className="p-2 text-xs">
                    {p.status === "pending" && (
                      <div className="flex gap-1">
                        <button onClick={() => updatePledgeStatus(p, "confirmed")} disabled={busy === `pl-${p.id}`}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-0.5 rounded text-xs"
                                data-testid={`sponsor-pledge-confirm-${p.id}`}>
                          Confirmer
                        </button>
                        <button onClick={() => updatePledgeStatus(p, "cancelled")}
                                className="bg-red-600 hover:bg-red-700 text-white px-2 py-0.5 rounded text-xs">
                          Annuler
                        </button>
                      </div>
                    )}
                    {p.status === "confirmed" && (
                      <button onClick={() => updatePledgeStatus(p, "active")}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-0.5 rounded text-xs">
                        Activer
                      </button>
                    )}
                    {p.status === "active" && (
                      <button onClick={() => updatePledgeStatus(p, "ended")}
                              className="bg-gray-600 hover:bg-gray-700 text-white px-2 py-0.5 rounded text-xs">
                        Clôturer
                      </button>
                    )}
                    {p.message && (
                      <div className="text-xs text-gray-500 italic mt-1 max-w-xs truncate" title={p.message}>
                        💬 {p.message.slice(0, 40)}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal formulaire enfant */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
             data-testid="sponsor-child-form-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {editing ? "Modifier l'enfant" : "Nouvel enfant à parrainer"}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-800">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <label className="flex flex-col">
                <span className="text-gray-600">Nom *</span>
                <input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded"
                       data-testid="sponsor-child-lastname" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Prénoms *</span>
                <input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded"
                       data-testid="sponsor-child-firstname" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Âge</span>
                <input type="number" min="3" max="25" value={form.age}
                       onChange={e => setForm({ ...form, age: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Classe</span>
                <input value={form.class_level} onChange={e => setForm({ ...form, class_level: e.target.value })}
                       placeholder="Première G2 / CE2 / 6ème…"
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col md:col-span-2">
                <span className="text-gray-600">École</span>
                <input value={form.school_name} onChange={e => setForm({ ...form, school_name: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Montant mensuel (FCFA)</span>
                <input type="number" min="100" step="100" value={form.monthly_need_fcfa}
                       onChange={e => setForm({ ...form, monthly_need_fcfa: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Objectif annuel (FCFA, optionnel)</span>
                <input type="number" min="0" value={form.yearly_target_fcfa}
                       onChange={e => setForm({ ...form, yearly_target_fcfa: e.target.value })}
                       placeholder="Auto = mensuel × 10 mois"
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col md:col-span-2">
                <span className="text-gray-600">Lien avec un(e) bénéficiaire</span>
                <input value={form.parent_relation}
                       onChange={e => setForm({ ...form, parent_relation: e.target.value })}
                       placeholder="Enfant de la veuve X, orphelin de Y…"
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col md:col-span-2">
                <span className="text-gray-600">Histoire (visible publiquement)</span>
                <textarea rows={4} value={form.story}
                          onChange={e => setForm({ ...form, story: e.target.value })}
                          placeholder="Une courte histoire qui donne envie de parrainer cet enfant…"
                          className="px-2 py-1 border border-gray-300 rounded" />
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowForm(false)}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded font-semibold">
                Annuler
              </button>
              <button onClick={submit} disabled={busy === "save"}
                      className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white px-4 py-2 rounded font-semibold"
                      data-testid="sponsor-child-save">
                {busy === "save" ? "Enregistrement…" : editing ? "Mettre à jour" : "Créer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
